import React from 'react';
import { FileText, ExternalLink, Plus, Loader2 } from 'lucide-react';
import { formatDate } from '../../../lib/dateUtils';

export const STATUS_META = {
  gap: { label: 'Gap', cls: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500' },
  due: { label: 'Due', cls: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
  draft: { label: 'Draft ready', cls: 'bg-sky-50 text-sky-700 border-sky-200', dot: 'bg-sky-500' },
  invoiced: { label: 'Invoiced', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  lapsed: { label: 'Possibly stopped', cls: 'bg-slate-100 text-slate-600 border-slate-200', dot: 'bg-slate-400' },
  ended: { label: 'Retainer ended', cls: 'bg-slate-100 text-slate-500 border-slate-200', dot: 'bg-slate-300' },
  not_started: { label: 'Starts later', cls: 'bg-slate-100 text-slate-500 border-slate-200', dot: 'bg-slate-300' },
};
const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const CYCLE = { monthly: 'Monthly', quarterly: 'Quarterly', half_yearly: 'Half-yearly', yearly: 'Yearly' };

export default function RetainerGapRow({ row, checked, onCheck, onCreate, onOpen, busy }) {
  const meta = STATUS_META[row.status];
  const actionable = row.status === 'gap' || row.status === 'due';
  return (
    <li className={`flex flex-wrap sm:flex-nowrap items-center gap-3 px-3 py-3 transition-colors ${checked ? 'bg-fuchsia-50/60' : 'hover:bg-slate-50/70'}`} data-testid={`retainer-gap-row-${row.client_id}`}>
      <input type="checkbox" disabled={!actionable} checked={checked} onChange={() => onCheck(row.client_id)}
        className="w-4 h-4 accent-fuchsia-600 disabled:opacity-30 shrink-0" data-testid={`retainer-gap-check-${row.client_id}`} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-sm text-[#1a1a2e] truncate max-w-[260px]" title={row.client_name}>{row.client_name}</span>
          <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-md border ${meta.cls}`} data-testid={`retainer-gap-status-${row.client_id}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />{meta.label}
          </span>
          {row.inferred && <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-violet-50 text-violet-700 border border-violet-100" title="No retainer amount is set on this client; detected from past retainer invoices">No retainer amount set</span>}
        </div>
        <p className="text-[11px] text-slate-500 mt-0.5">
          {CYCLE[row.cycle]} · {row.period_label}
          {row.last_invoice_number ? ` · last ${row.last_invoice_number} on ${formatDate(row.last_invoice_date)} (${row.days_since_last}d ago)` : ' · never invoiced as retainer'}
          {row.retainer_end_date && row.status === 'ended' ? ` · ended ${formatDate(row.retainer_end_date)}` : ''}
        </p>
      </div>
      <div className="text-right shrink-0 w-28">
        <p className="text-sm font-semibold tabular-nums text-[#1a1a2e]">{inr(row.retainer_amount)}</p>
        <p className="text-[10px] text-slate-500">{row.amount_source === 'retainer' ? 'retainer amount' : 'from last invoice'}</p>
      </div>
      <div className="shrink-0 w-36 flex justify-end">
        {actionable && (
          <button type="button" onClick={() => onCreate([row.client_id])} disabled={busy}
            className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-xs font-semibold active:scale-95 transition disabled:opacity-60"
            data-testid={`retainer-gap-create-${row.client_id}`}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Draft invoice
          </button>
        )}
        {(row.status === 'draft' || row.status === 'invoiced') && row.invoice && (
          <button type="button" onClick={() => onOpen(row.invoice._id)}
            className="inline-flex items-center gap-1 h-8 px-3 rounded-full border border-slate-200 hover:border-fuchsia-300 text-xs font-semibold text-slate-700"
            data-testid={`retainer-gap-open-${row.client_id}`}>
            {row.status === 'draft' ? <FileText className="w-3.5 h-3.5" /> : <ExternalLink className="w-3.5 h-3.5" />}
            {row.status === 'draft' ? 'Open draft' : row.invoice.invoice_number}
          </button>
        )}
      </div>
    </li>
  );
}
